import { Box, Button, IconButton, Modal, Stack, Typography } from '@mui/material';
import { CloseRounded } from '@mui/icons-material';
import { Trans } from 'react-i18next';
import { palette } from '../../../styles/palette';
import { RebateProgramStatusUpdateRequest, Styles } from '../../../types';
import { useEntityForm, useInternationalization } from '../../../hooks';
import { ControlledTranslatedInput } from '../../Form';
import { VendorRebateCategoryProgramChangeReasonEntity } from '../../../models/Vendor/VendorRebateCategoryProgramChangeReasonEntity';
import { VendorRebateCategoryProgramChangeReasonSchema } from '../../../schemas/Vendor/VendorRebateCategoryProgramChangeReasonSchema';
import { EntityFormProvider } from '../../../contexts';
import { useMemo, useState } from 'react';
import { LoadingButton } from '@mui/lab';

const style: Styles = {
  container: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: palette.white,
    p: 3,
    width: {
      xs: '90%',
      md: 'auto',
    },
    minWidth: '400px',
    minHeight: '150px',
    borderRadius: '15px',
  },
  close: {
    mt: '-18px !important',
    mr: '-18px !important',
  },
  reasonHelpText: {
    mt: 1,
  },
};

export interface VendorRebateCategoryProgramApproveModalProps {
  open: boolean;
  onConfirm?: (reason?: RebateProgramStatusUpdateRequest) => Promise<void>;
  onClose: () => void;
  name: string;
  action: string;
  reasonRequired: boolean;
}

export const VendorRebateCategoryProgramApproveModal = ({
  open,
  onConfirm,
  onClose,
  name,
  action,
  reasonRequired,
}: VendorRebateCategoryProgramApproveModalProps) => {
  const [isSaving, setIsSaving] = useState(false);
  const { t } = useInternationalization();
  const confirmText = t('common:action.confirmButton', { action });
  const title = t('common:action.confirmTitle', {
    name: t('vendor:rebateCategory.programs.actions.suffix'),
    action,
    count: 1,
  });
  const description = <Trans i18nKey="common:action.confirmDescription" values={{ name, count: 1 }} />;
  const changeReason: VendorRebateCategoryProgramChangeReasonEntity = useMemo(() => {
    return new VendorRebateCategoryProgramChangeReasonEntity();
  }, []);
  const { form, control, getValues, reset, handleError } = useEntityForm<VendorRebateCategoryProgramChangeReasonEntity>(
    changeReason,
    VendorRebateCategoryProgramChangeReasonSchema(reasonRequired),
  );
  const handleConfirm = async () => {
    if (onConfirm && !isSaving) {
      try {
        setIsSaving(true);
        if (reasonRequired) {
          const { changeReason_En, changeReason_Fr } = getValues();
          await onConfirm({ changeReason_En, changeReason_Fr } as RebateProgramStatusUpdateRequest);
        } else {
          await onConfirm();
        }
        handleClose();
      } catch (error) {
        handleError(error);
      } finally {
        setIsSaving(false);
      }
    }
  };
  const handleClose = () => {
    onClose();
    reset();
  };
  return (
    <Modal open={open} onClose={handleClose}>
      <Stack sx={style.container} spacing={5} direction="column" justifyContent="space-between">
        <Stack spacing={1}>
          <Stack spacing={5} direction="row" alignItems="flex-start" justifyContent="space-between">
            <Typography variant="modalTitle">{title}</Typography>
            <IconButton id="close" onClick={handleClose} sx={style.close}>
              <CloseRounded htmlColor={palette.primary.deep} fontSize="large" />
            </IconButton>
          </Stack>
          <Typography variant="modalDescription">{description}</Typography>
        </Stack>
        <Box>
          <EntityFormProvider {...form}>
            {reasonRequired && (
              <>
                <ControlledTranslatedInput
                  label={t('vendor:rebateCategory.programs.changeReason')}
                  name="changeReason"
                  control={control}
                  required
                />
                <Box sx={style.reasonHelpText}>
                  <Trans i18nKey="vendor:rebateCategory.programs.changeReasonHelp" />
                </Box>
              </>
            )}
          </EntityFormProvider>
        </Box>
        <Stack spacing={1} direction="row" justifyContent="flex-end">
          <Button id="cancel" onClick={handleClose} variant="outlined">
            {t('common:cancel')}
          </Button>
          <LoadingButton id="confirm" onClick={handleConfirm} loading={isSaving} variant="contained">
            {confirmText}
          </LoadingButton>
        </Stack>
      </Stack>
    </Modal>
  );
};
